// alarmAudio.js
// Camada de áudio do alarme crítico.
//
// O timbre é sintetizado via Web Audio em vez de carregado de um arquivo: não
// há asset binário no repositório e a calibragem (frequências, pulsação,
// volume) fica parametrizável. Houve uma versão com arquivo mp3 de 23 s; foi
// descartada por julgamento de quem ouviu.
//
// O padrão é o hi-lo de supervisório: uma portadora quadrada que alterna entre
// duas alturas, recortada em bipes por um segundo oscilador. Tudo por
// oscilador, sem timer da aplicação.
//
// Os navegadores da operação bloqueiam reprodução automática: o contexto só sai
// de "suspended" após gesto explícito do operador. É por isso que `enable()`
// existe e que o estado bloqueado é observável — alarme silencioso sem o
// operador saber é o pior desfecho possível desta feature.

export const AUDIO_ACTIVE = "ativo";
export const AUDIO_BLOCKED = "bloqueado";
export const AUDIO_MUTED = "silenciado";
export const AUDIO_UNSUPPORTED = "indisponivel";

export const DEFAULT_SETTINGS = {
  // Duas frequências alternadas em vez de um tom fixo. O padrão hi-lo é o que
  // a operação reconhece como alarme de supervisório; um tom único com ganho
  // ondulando soa como zumbido de equipamento, não como chamado de atenção.
  // A faixa 660–990 Hz atravessa ruído de campo e vaza bem em alto-falante
  // pequeno de estação de trabalho.
  lowHz: 660,
  highHz: 990,
  // Bipes por segundo. Perto de 3 Hz é a faixa lida como urgente sem virar
  // ruído contínuo; a alternância de tom acontece na metade disso, para que
  // cada bipe tenha uma altura só.
  pulseHz: 3,
  volume: 0.6,
};

// A aplicação é exclusivamente de navegador, então `window` sempre existe aqui.
const resolveAudioContext = () =>
  window.AudioContext || window.webkitAudioContext;

export const createAlarmAudio = (overrides = {}) => {
  const settings = { ...DEFAULT_SETTINGS, ...overrides };
  const AudioContextImpl = settings.AudioContextImpl || resolveAudioContext();

  let ctx = null;
  let nodes = null;
  let muted = false;
  let volume = settings.volume;

  const supported = Boolean(AudioContextImpl);

  const status = () => {
    if (!supported) return AUDIO_UNSUPPORTED;
    if (!ctx || ctx.state !== "running") return AUDIO_BLOCKED;
    if (muted) return AUDIO_MUTED;
    return AUDIO_ACTIVE;
  };

  // O LFO SOMA-SE ao valor intrínseco do AudioParam, então base e profundidade
  // valem metade do volume: o ganho oscila entre 0 e `volume`, encostando em
  // zero a cada ciclo (o que produz o bipe) sem estourar acima de 1.
  const gainValue = () => (muted ? 0 : volume / 2);

  /**
   * Libera o áudio. Precisa ser chamada de dentro de um gesto do operador —
   * fora disso o navegador mantém o contexto suspenso e o estado segue
   * bloqueado.
   */
  const enable = async () => {
    if (!supported) return AUDIO_UNSUPPORTED;
    try {
      if (!ctx) ctx = new AudioContextImpl();
      if (ctx.state !== "running") await ctx.resume();
    } catch {
      // Gesto recusado ou contexto indisponível: permanece bloqueado, e o
      // indicador da interface mostra isso ao operador.
    }
    return status();
  };

  const isPlaying = () => nodes !== null;

  /**
   * Inicia o sinal em loop. É idempotente de propósito: uma rajada de vários
   * ativos entrando em crítico ao mesmo tempo produz um único sinal sonoro,
   * sem sobreposição.
   */
  const start = () => {
    if (!supported || !ctx || ctx.state !== "running" || nodes) return status();

    const carrier = ctx.createOscillator();
    const gateLfo = ctx.createOscillator();
    const toneLfo = ctx.createOscillator();
    const gateDepth = ctx.createGain();
    const toneDepth = ctx.createGain();
    const master = ctx.createGain();

    // Onda quadrada: rica em harmônicos, corta ruído de fundo e é a assinatura
    // sonora que a operação associa a alarme, não a notificação.
    carrier.type = "square";
    carrier.frequency.value = (settings.lowHz + settings.highHz) / 2;

    // Recorte do bipe. Quadrada, e não senoidal: a senoide faz o ganho subir e
    // descer suavemente, o que soa como um tom ondulando. A quadrada só assume
    // -1 ou +1, então o ganho pula entre zero e o volume cheio — bipe com
    // ataque e corte secos, que é o que se ouve num supervisório.
    gateLfo.type = "square";
    gateLfo.frequency.value = settings.pulseHz;

    // Alternância hi-lo, na METADE da taxa do recorte: cada bipe sai inteiro
    // numa altura só, e o par de bipes forma o padrão de duas notas.
    toneLfo.type = "square";
    toneLfo.frequency.value = settings.pulseHz / 2;

    // O LFO SOMA-SE ao valor intrínseco do AudioParam. Base e profundidade
    // valem metade do volume, então o ganho alterna exatamente entre 0 e
    // `volume` — sem estourar acima de 1 e sem clipping na portadora square.
    gateDepth.gain.value = gainValue();
    master.gain.value = gainValue();

    // Mesma lógica na frequência: a portadora fica no ponto médio e o LFO a
    // desloca meia distância para cada lado, aterrissando em lowHz e highHz.
    toneDepth.gain.value = (settings.highHz - settings.lowHz) / 2;

    gateLfo.connect(gateDepth);
    gateDepth.connect(master.gain);

    toneLfo.connect(toneDepth);
    toneDepth.connect(carrier.frequency);

    carrier.connect(master);
    master.connect(ctx.destination);

    carrier.start();
    gateLfo.start();
    toneLfo.start();

    nodes = { carrier, gateLfo, toneLfo, gateDepth, toneDepth, master };
    return status();
  };

  const stop = () => {
    if (!nodes) return status();
    const { carrier, gateLfo, toneLfo, gateDepth, toneDepth, master } = nodes;
    nodes = null;
    [carrier, gateLfo, toneLfo].forEach((osc) => {
      try {
        osc.stop();
      } catch {
        // Oscilador já encerrado pelo navegador: nada a fazer.
      }
      osc.disconnect();
    });
    gateDepth.disconnect();
    toneDepth.disconnect();
    master.disconnect();
    return status();
  };

  const applyGain = () => {
    if (!nodes) return;
    nodes.master.gain.value = gainValue();
    nodes.gateDepth.gain.value = gainValue();
  };

  const setMuted = (value) => {
    muted = Boolean(value);
    applyGain();
    return status();
  };

  const setVolume = (value) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return status();
    volume = Math.min(1, Math.max(0, parsed));
    applyGain();
    return status();
  };

  const getVolume = () => volume;
  const isMuted = () => muted;

  const dispose = () => {
    stop();
    if (ctx) {
      try {
        ctx.close();
      } catch {
        // Contexto já encerrado.
      }
      ctx = null;
    }
  };

  return {
    enable,
    start,
    stop,
    setMuted,
    setVolume,
    getVolume,
    isMuted,
    isPlaying,
    status,
    dispose,
  };
};
